// Copyright 2026 AllSpice. All rights reserved. Not Gitea.
// SPDX-License-Identifier: UNLICENSED

// RFC 4180 CSV for the derating sheets DRCY publishes: quoted fields may hold
// commas, quotes and line breaks. Parsing keeps the source's line terminator,
// trailing-newline choice and ragged rows so an untouched table serializes back
// byte for byte.

export type CsvNewline = '\n' | '\r\n';

export type CsvTable = {
  header: string[];
  rows: string[][];
  newline: CsvNewline;
  trailingNewline: boolean;
};

export function parseCsv(text: string): CsvTable {
  const newline: CsvNewline = text.includes('\r\n') ? '\r\n' : '\n';
  if (text === '') return {header: [], rows: [], newline, trailingNewline: false};
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let quoted = false;
  let endedRecord = true;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      record.push(field);
      field = '';
      endedRecord = false;
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
      endedRecord = true;
    } else {
      field += ch;
      endedRecord = false;
    }
  }
  const trailingNewline = text.length > 0 && endedRecord && field === '' && record.length === 0;
  if (!trailingNewline || quoted) {
    record.push(field);
    records.push(record);
  }

  // Rows stay as ragged as the source: padding them here would turn a blank
  // line into a row of empty cells and add trailing commas to short rows, so
  // one cell edit would rewrite unrelated lines.
  const [header = [], ...rows] = records;
  return {header, rows, newline, trailingNewline};
}

// Quotes only what RFC 4180 requires, matching the minimal quoting DRCY's own
// writer uses so a round trip through the table does not churn the sheet.
export function csvField(value: string): string {
  if (/[",\r\n]/.test(value)) return `"${value.replaceAll('"', '""')}"`;
  return value;
}

export function serializeCsv(table: CsvTable): string {
  const lines = [table.header, ...table.rows].map((cells) => cells.map(csvField).join(','));
  if (lines.length === 0) return '';
  return lines.join(table.newline) + (table.trailingNewline ? table.newline : '');
}

// The table with one cell replaced. Only the edited row grows to reach its
// column; other short rows stay as the source wrote them, so one edit never
// rewrites unrelated lines. A row index past the sheet leaves it unchanged.
export function setCsvCell(table: CsvTable, row: number, column: number, value: string): CsvTable {
  const current = Number.isInteger(row) ? table.rows[row] : undefined;
  if (!current || !Number.isInteger(column) || column < 0) return table;
  const edited = [...current, ...new Array<string>(Math.max(0, column + 1 - current.length)).fill('')];
  edited[column] = value;
  return {...table, rows: table.rows.map((cells, i) => (i === row ? edited : cells))};
}
